import type { LessonDataSource, LessonDataSourceCounts } from "./slideCount.js";

/**
 * Lines the author edited for one source before generating. They stand in for the book's own lines
 * for that run only — nothing is written back to the book, and a saved preset never carries them.
 */
export interface ContextLines {
  source: LessonDataSource;
  lines: string[];
}

/**
 * The author's lines as typed: blank ones are what separate one passage from the next, so they are
 * kept between lines and dropped only at the ends, and a run of them counts as one.
 */
export function tidyLines(lines: string[]): string[] {
  const tidied = lines
    .map((line) => line.trim())
    .filter((line, index, all) => line !== "" || all[index - 1]?.trim() !== "");

  while (tidied[0] === "") tidied.shift();
  while (tidied.at(-1) === "") tidied.pop();
  return tidied;
}

/**
 * Lines the author edited for one part of the lesson — a book section and everything printed under
 * it. They stand in for what the book prints there on the same terms as ContextLines: this run only.
 */
export interface PartLines {
  section: string;
  lines: string[];
}

/** The author's lines for a source, or undefined where they left the book's alone. */
export function editedLines(edits: ContextLines[], source: LessonDataSource): string[] | undefined {
  return edits.find((one) => one.source === source)?.lines;
}

/** The author's lines for a part, or undefined where they left the book's alone. */
export function editedPartLines(edits: PartLines[], section: string): string[] | undefined {
  return edits.find((one) => one.section === section)?.lines;
}

/** What a section counts against: its own lines where it has them, the book's everywhere else. */
export function countsWithEdits(
  counts: LessonDataSourceCounts,
  edits: ContextLines[]
): LessonDataSourceCounts {
  const merged = { ...counts };
  for (const edit of edits) {
    merged[edit.source] = edit.lines.filter((line) => line.trim() !== "").length;
  }
  return merged;
}
